import type { AnyAction } from '@reduxjs/toolkit'

import { entityReducer } from 'syft-acp-core/reducers/generators/entityReducer'
import * as entityTypes from 'syft-acp-core/reducers/generators/entities'
import { apiErrorMessage } from 'syft-acp-core/reducers/generators/utils'

import {
  AGENCY_CASCADE_SCHEDULING_PLAN_FETCH_BEGIN,
  AGENCY_CASCADE_SCHEDULING_PLAN_FETCH_FAILED,
  AGENCY_CASCADE_SCHEDULING_PLAN_FETCH_SUCCEEDED,
} from './action-types'
import type { SchedulingPlan, SchedulingPlanRequestState, SchedulingPlansState } from './types'

const baseReducer = entityReducer<SchedulingPlan, Pick<SchedulingPlansState, 'requestStateByPlanId'>>(
  'agencyCascadeSchedulingPlans',
  {
    [entityTypes.ENTITY_FETCH_BEGIN]: AGENCY_CASCADE_SCHEDULING_PLAN_FETCH_BEGIN,
    [entityTypes.ENTITY_FETCH_SUCCEEDED]: AGENCY_CASCADE_SCHEDULING_PLAN_FETCH_SUCCEEDED,
    [entityTypes.ENTITY_FETCH_FAILED]: AGENCY_CASCADE_SCHEDULING_PLAN_FETCH_FAILED,
  },
  {
    optionsKey: 'planId',
    initialState: { requestStateByPlanId: {} },
  },
)

const updateRequestState = (
  state: SchedulingPlansState,
  planId: number,
  requestState?: SchedulingPlanRequestState,
): SchedulingPlansState => {
  const requestStateByPlanId = { ...state.requestStateByPlanId }
  if (requestState) requestStateByPlanId[planId] = requestState
  else delete requestStateByPlanId[planId]

  return {
    ...state,
    isLoadingData: Object.values(requestStateByPlanId).some(request => request.isLoading),
    requestStateByPlanId,
  }
}

export const reducer = (state: SchedulingPlansState | undefined, action: AnyAction): SchedulingPlansState => {
  const nextState = baseReducer(state, action)

  switch (action.type) {
    case AGENCY_CASCADE_SCHEDULING_PLAN_FETCH_BEGIN:
      return updateRequestState(nextState, action.payload.planId, { isLoading: true })
    case AGENCY_CASCADE_SCHEDULING_PLAN_FETCH_SUCCEEDED:
      return updateRequestState(nextState, action.options.planId)
    case AGENCY_CASCADE_SCHEDULING_PLAN_FETCH_FAILED:
      return updateRequestState(nextState, action.request.payload.planId, {
        isLoading: false,
        error:
          apiErrorMessage({ body: action.payload.body, message: action.message }) ||
          'Unable to load the scheduling plan.',
      })
    default:
      return nextState
  }
}
